import type { ComponentType } from "react";
import { useMemo } from "react";

import classNames from "classnames";
import mapValues from "lodash/mapValues";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { CardGroup, MetricCard } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, FlexRow, PixelPlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minimumValueChangeFilter, minVisits, spendFilter } from "app/filters/ngFilters";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { PerformanceResponse } from "app/pages/digital-marketing/ad-intelligence/common/overview/types";
import DurationService from "app/services/DurationService";
import type { dateForTooltip } from "app/services/DurationService";

import { EDataType } from "../../advertisers/overview/components/types";
import type { AdIntelligenceController } from "../shared/types/types";
import { NA_VALUE, obfuscateData } from "../utils/utils";

const LoaderWrapper = styled(FlexRow)`
    width: 100%;
    gap: 24px;
`;

const MetricTooltipContainer = styled.div`
    max-width: 218px;
`;

const MetricTitleWrapper = styled(Flex)`
    margin-right: 8px;
`;

const MetricTitleInfoIcon = styled(Flex)`
    line-height: 0;
`;

const UPDATED_BADGE_METRICS = [EDataType.SPEND, EDataType.IMPRESSIONS];

export const UpdatedMetricBadge = () => (
    <Tooltip
        content={
            <MetricTooltipContainer>
                {translate("ad.intelligence.overview.performance.widget.updated.badge.tooltip")}
            </MetricTooltipContainer>
        }
    >
        <Flex
            inline
            alignItems="center"
            data-automation="performance-widget-updated-badge"
        >
            <Badge type="neutral">{translate("ad.intelligence.overview.performance.widget.updated.badge")}</Badge>
        </Flex>
    </Tooltip>
);

const MetricTitleWithUpdatedBadge = ({ title, tooltip }: { title: string; tooltip: string }) => (
    <MetricTitleWrapper
        inline
        alignItems="center"
        gap="8px"
    >
        <Text
            fontVariant="subTitle3Alt"
            color={colorsPalettes.carbon[400]}
        >
            {title}
        </Text>
        <Tooltip content={<MetricTooltipContainer>{tooltip}</MetricTooltipContainer>}>
            <MetricTitleInfoIcon inline>
                <SWReactIcons
                    iconName="info"
                    size="xs"
                />
            </MetricTitleInfoIcon>
        </Tooltip>
        <UpdatedMetricBadge />
    </MetricTitleWrapper>
);

interface IPerformanceOverviewWidgetProps {
    data?: PerformanceResponse;
    isLoading: boolean;
    controller: AdIntelligenceController;
    isWindow: boolean;
    withUpdatedBadge?: boolean;
}

type MetricData = {
    value: number;
    change: number;
    desktopShare: number;
};

interface IMetricConfig {
    dataKey: EDataType;
    formatter: (value: number) => string;
}
const getMetricConfigs = (controller: AdIntelligenceController) => [
    {
        dataKey: EDataType.SPEND,
        formatter: (value: number) => spendFilter(value, abbrNumberFilter(), 100),
    },
    {
        dataKey: EDataType.IMPRESSIONS,
        formatter: (value: number) => minVisits(value, 1000, abbrNumberFilter(), NA_VALUE),
    },
    ...(controller === "advertiser"
        ? [
              {
                  dataKey: EDataType.VISITS,
                  formatter: (value: number) => minVisits(value, 1000, abbrNumberFilter(), NA_VALUE),
              },
          ]
        : [
              {
                  dataKey: EDataType.PAGE_VIEWS,
                  formatter: (value: number) => minVisits(value, 1000, abbrNumberFilter(), NA_VALUE),
              },
          ]),
];
export const getDurationForTooltip = (duration: string) => {
    return mapValues(DurationService.getDurationData(duration).forTooltip as dateForTooltip, (item) => {
        return decodeURIComponent(item);
    });
};

export const PerformanceOverviewWidget = ({
    data = {} as PerformanceResponse,
    isLoading,
    controller,
    isWindow,
    withUpdatedBadge = false,
}: IPerformanceOverviewWidgetProps) => {
    const { duration } = useDisplayAdvertiserPublisherDataAndParams();
    const thirdMetric = controller === "advertiser" ? EDataType.VISITS : EDataType.PAGE_VIEWS;

    const updatedBadgeTitleComponents = useMemo(() => {
        if (!withUpdatedBadge) {
            return {};
        }

        return UPDATED_BADGE_METRICS.reduce<Partial<Record<EDataType, ComponentType>>>((titleComponents, dataKey) => {
            const title = translate(`ad.intelligence.overview.performance.widget.${dataKey}.title`);
            const tooltip = translate(`ad.intelligence.${controller}.overview.performance.widget.${dataKey}.tooltip`);
            titleComponents[dataKey] = () => (
                <MetricTitleWithUpdatedBadge
                    title={title}
                    tooltip={tooltip}
                />
            );
            return titleComponents;
        }, {});
    }, [withUpdatedBadge, controller]);

    const renderMetricCard = (config: IMetricConfig) => {
        const titleComponent = updatedBadgeTitleComponents[config.dataKey];
        const metricData = data[config.dataKey] as MetricData;

        const { value, change } = obfuscateData(
            metricData?.value,
            metricData?.change,
            data.hasAdIntelMetrics,
            data?.[thirdMetric]?.value,
        );

        const displayedValue = value ? config.formatter(value) : NA_VALUE;
        const displayedChange = change;

        const blurredValueClassName = classNames("blurable-cell", { "no-data": !metricData?.value });

        return (
            <MetricCard
                key={config.dataKey}
                withoutShadow
                title={translate(`ad.intelligence.overview.performance.widget.${config.dataKey}.title`)}
                titleComponent={titleComponent}
                tooltip={
                    titleComponent ? undefined : (
                        <MetricTooltipContainer>
                            {translate(
                                `ad.intelligence.${controller}.overview.performance.widget.${config.dataKey}.tooltip`,
                            )}
                        </MetricTooltipContainer>
                    )
                }
                value={
                    <span className={config.dataKey !== thirdMetric ? blurredValueClassName : ""}>
                        {displayedValue}
                    </span>
                }
                {...(displayedChange && {
                    changeValueProps: {
                        value: minimumValueChangeFilter()(displayedChange, 1),
                        isDecrease: metricData?.change < 0,
                        className: config.dataKey !== thirdMetric ? blurredValueClassName : "",
                        tooltipProps: {
                            enabled: true,
                            tooltipContent: isWindow
                                ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                                : translate(
                                      `ad.intelligence.overview.performance.widget.${config.dataKey}.change.tooltip`,
                                      getDurationForTooltip(duration),
                                  ),
                        },
                    },
                })}
            />
        );
    };

    return isLoading ? (
        <LoaderWrapper>
            <PixelPlaceholderLoader
                width="100%"
                height={98}
            />
            <PixelPlaceholderLoader
                width="100%"
                height={98}
            />
            <PixelPlaceholderLoader
                width="calc(100% - 24px)"
                height={98}
            />
        </LoaderWrapper>
    ) : (
        <CardGroup
            $height="52px"
            groupDirection="row"
            withDividers
        >
            {getMetricConfigs(controller).map(renderMetricCard)}
        </CardGroup>
    );
};
